import { SyncHealthBanner } from '@/components/admin/SyncHealthBanner';
import { SyncHistorySection } from '@/components/admin/SyncHistorySection';
import { SyncOrgsTable } from '@/components/admin/SyncOrgsTable';
import { SyncProblemsSection } from '@/components/admin/SyncProblemsSection';
import { useOrganizationsSyncStats, useSyncHistory, useSyncOverview } from '@/hooks/useSyncMonitoring';
import {
  detectOrgProblems,
  deriveSyncHealth,
  groupSyncHistory,
} from '@/lib/sync-monitoring';
import { useMemo } from 'react';

/**
 * Exception-first health view of the Sewan → local syncs:
 * 1. Global health banner (OK / à surveiller / en échec) + global actions
 * 2. "À traiter": only the orgs with a problem, one action each
 * 3. Recent executions, cron runs grouped by night, expandable
 * 4. Exhaustive per-org table, collapsed by default
 */
export function SyncMonitoringView() {
  const {
    data: overview,
    isLoading: overviewLoading,
    isError: overviewError,
  } = useSyncOverview();
  const {
    data: orgs,
    isLoading: orgsLoading,
    isError: orgsError,
  } = useOrganizationsSyncStats();
  const {
    data: history,
    isLoading: historyLoading,
    isError: historyError,
  } = useSyncHistory(200);

  const problems = useMemo(() => detectOrgProblems(orgs ?? []), [orgs]);
  const health = overview ? deriveSyncHealth(overview, problems) : null;
  const historyItems = useMemo(
    () => groupSyncHistory(history ?? []),
    [history]
  );

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">
          Synchronisation Sewan
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          État du miroir local de Sewan : organisations, centres de facturation
          et lignes. Le sync nocturne tourne chaque nuit à 2h (détails puis
          lignes).
        </p>
      </div>

      <SyncHealthBanner
        overview={overview}
        health={health}
        loading={overviewLoading || orgsLoading}
        error={overviewError}
      />

      {!orgsLoading && !orgsError && (
        <SyncProblemsSection problems={problems} />
      )}

      <SyncHistorySection
        items={historyItems}
        loading={historyLoading}
        error={historyError}
      />

      <SyncOrgsTable orgs={orgs} loading={orgsLoading} error={orgsError} />
    </div>
  );
}
